import { useState, useMemo, useCallback } from 'react';
import { 
    getDetailedWorkerConflicts, 
    getDetailedVehicleConflicts, 
    getLogicalConflictCount 
} from '../../../../utils/leaderUtils';

/**
 * Custom hook to handle conflict detection logic and state persistence.
 * 
 * @param {Object} options
 * @param {Array} options.workers - List of workers
 * @param {Array} options.vehicles - List of vehicles
 * @param {Array} options.assignmentsForConflicts - Assignments used for conflict calculation
 * @param {Array} options.absencesForConflicts - Absences used for conflict calculation
 * @param {Array} options.kolonnen - List of kolonnen
 * @returns {Object} { calculatedConflicts, logicalConflictCount, isConflictDetectionEnabled, handleToggleConflictDetection, conflictStates... }
 */
export function useLeaderConflicts({ 
    workers, 
    vehicles, 
    assignmentsForConflicts, 
    absencesForConflicts, 
    kolonnen 
}) {
    /**
     * Centralized conflict calculation for all resources (Workers & Vehicles).
     * Reacts immediately to data changes in assignmentsForConflicts / absencesForConflicts.
     */
    const calculatedConflicts = useMemo(() => {
        const workerConflicts = (workers || []).flatMap(w => 
            getDetailedWorkerConflicts(w.id, w.name, w.role, assignmentsForConflicts, absencesForConflicts)
        );
        const vehicleConflicts = (vehicles || []).flatMap(v => 
            getDetailedVehicleConflicts(v.id, v, v.internal_name, assignmentsForConflicts)
        );
        return [...workerConflicts, ...vehicleConflicts];
    }, [workers, vehicles, assignmentsForConflicts, absencesForConflicts]);

    /**
     * Total count of logical conflicts, considering kolonnen grouping.
     */
    const logicalConflictCount = useMemo(() => {
        return getLogicalConflictCount(calculatedConflicts, kolonnen);
    }, [calculatedConflicts, kolonnen]);

    /**
     * Toggle state for conflict detection, persisted in sessionStorage.
     */
    const [isConflictDetectionEnabled, setIsConflictDetectionEnabled] = useState(() => {
        const saved = sessionStorage.getItem('isConflictDetectionEnabled');
        return saved === null ? true : saved === 'true';
    });

    const handleToggleConflictDetection = useCallback((newValue) => {
        setIsConflictDetectionEnabled(newValue);
        sessionStorage.setItem('isConflictDetectionEnabled', newValue);
    }, []);

    // --- Conflict Overview State Persistence ---
    const [conflictActiveTab, setConflictActiveTab] = useState('resources');
    const [conflictExpandedResources, setConflictExpandedResources] = useState(new Set());
    const [conflictCollapsedSections, setConflictCollapsedSections] = useState(new Set(['kolonnen', 'mitarbeiter', 'fahrzeuge']));

    return {
        calculatedConflicts,
        logicalConflictCount,
        isConflictDetectionEnabled,
        handleToggleConflictDetection,
        conflictActiveTab,
        setConflictActiveTab,
        conflictExpandedResources,
        setConflictExpandedResources,
        conflictCollapsedSections,
        setConflictCollapsedSections
    };
}
